// cSpell:ignore Aandeel Afrekeningen Bedrag Betaald Betaalstatus Duizendsten gebouw Gebouw GEBOUW huurder Kost laste Periode Privaat PRIVAAT privaat Wijzigen
/* eslint-disable no-nested-ternary */
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  DropdownMenu,
  EmptyState,
  Filters,
  Icon,
  Lozenge,
  Pagination,
  Select,
  Tooltip,
} from "@rentiohq/web-shared/dist/components";
import React, { useMemo, useState } from "react";
import styled from "styled-components";
import {
  costListTotals,
  ICostRowLevel,
  sortCostsNewestFirst,
} from "../costLevels.utils";
import {
  costChargedToTexts,
  costListColumnAlign,
  costListColumnHeader,
  costListColumns,
  costListGridTemplate,
  costListRowCells,
  ECostListColumn,
  ICostListCell,
} from "../costListColumns.utils";
import { periodRangeLines } from "../costPeriod.utils";
import {
  ICostSettledInfo,
  isPartlySettledAtDeparture,
} from "../costSettlementState.utils";
import {
  costListStatus,
  costMatchesSearch,
  costMatchesStatusFilter,
  ECostStatusFilter,
} from "../costsList.utils";
import { costInYear, costYears, ECostYearBasis } from "../costYear.utils";
import { TGroupCostUnitShare } from "../groupCostShare.utils";
import { OrderPaymentStatus } from "../hooks";
import { todayIsoDay } from "../localDay.utils";
import { formatMoney } from "../moneyText.utils";
import { Invoice, sortedCategoryOptions, isTenantPaidStatus } from "../types";
import {
  ListAligned,
  ListAmount,
  ListCell,
  ListCellText,
  ListFilterRow,
  ListFrame,
  ListHeaderCell,
  ListHeaderRow,
  ListLozengeBox,
  ListMenuButton,
  ListOneLine,
  ListRow,
  ListScreenReaderText,
  ListSecondary,
  ListSubLine,
  ListTitle,
  ListToolBar,
} from "./listTable.styles";
import { ListTypeLozenge } from "./ListTypeLozenge";
import { TypeColumnHeader } from "./TypeColumnHeader";

// Fill each split's `paidAt` from the LIVE payment-request status, so a cost's
// Betaalstatus follows the real payment (paid online or marked paid) exactly
// like a settlement does — instead of relying on the stored copy, which only
// gets synced for the manual-paid path. Uses the request's real paid date;
// falls back to today only when the request carries no date.
export const withLivePaidSplits = (
  inv: Invoice,
  statusByOrderId?: Record<number, OrderPaymentStatus>,
  // Bulk mode shares ONE payment order across payers (one request per payer
  // under it) — the split's own paymentRequestId is then the only precise
  // per-payer handle. When given, it wins over the order-level status so one
  // tenant paying doesn't mark every split paid.
  statusByRequestId?: Record<number, OrderPaymentStatus>,
): Invoice => {
  const splits = inv.paymentSplits;
  if (!statusByOrderId || !splits || splits.length === 0) return inv;
  let changed = false;
  const next = splits.map(s => {
    if (s.paidAt || s.paymentOrderId == null) return s;
    const useRequestLevel =
      statusByRequestId != null && s.paymentRequestId != null;
    const entry = useRequestLevel
      ? statusByRequestId[Number(s.paymentRequestId)]
      : statusByOrderId[Number(s.paymentOrderId)];
    if (entry == null || !isTenantPaidStatus(entry.status)) return s;
    changed = true;
    return {
      ...s,
      paidAt: entry.paidAt ? entry.paidAt.slice(0, 10) : todayIsoDay(),
    };
  });
  return changed ? { ...inv, paymentSplits: next } : inv;
};

const PAGE_SIZE = 20;

// The columns and their widths: see costListColumns.utils.
const Wrap = styled(ListFrame)`
  overflow-x: auto;
`;

// Labelled the way the settlement bills the cost (withBilledTarget).
export const distributionLozenge = (invoice: Invoice) => {
  const { text, split } = costChargedToTexts(invoice);

  return (
    <Tooltip tooltipContent={split}>
      <span>
        <Lozenge appearance="default">{text}</Lozenge>
      </span>
    </Tooltip>
  );
};

// Whether a confirmed settlement settled the cost, or departure settlements
// part of it (see costSettlementState.utils).
export type SettledInfo = ICostSettledInfo;

const statusOf = (invoice: Invoice, settled: SettledInfo) =>
  costListStatus({
    invoice,
    settled: settled.settled,
    partlySettled: isPartlySettledAtDeparture(settled),
  });

// What the list shows after its search and filters, for the card heading
// above it (count and total of the shown rows; see costListTotals).
export interface ICostsTableView {
  count: number;
  totalExcl: number;
  // Shown building costs whose share of the unit is still to be worked out.
  pendingShareCount: number;
  // What the building's costs add to `totalExcl`, inside a building.
  buildingTotalExcl: number;
  // A search or a filter is active.
  isFiltered: boolean;
}

export interface CostsTableProps {
  invoices: Invoice[];
  // Inside a building: the Type column, "Gebouw" or "Privaat", a private
  // cost's unit by its address in the tooltip (see costRowLevel). Left out
  // (a unit on its own, the overview), the list has no Type column.
  rowLevelOf?: (inv: Invoice) => ICostRowLevel | undefined;
  // A list across properties shows each row's property where the Type
  // column would be.
  propertyLabelFor?: (inv: Invoice) => string | undefined;
  settledInfoFor: (inv: Invoice) => SettledInfo;
  onView: (inv: Invoice) => void;
  // Left out for a user who may only read: the row then only offers "view".
  onEdit?: (inv: Invoice) => void;
  onDelete?: (inv: Invoice) => void;
  // Rows another page manages (a building's cost on a unit page) are only
  // read here: no edit and no delete, but `editElsewhere` when given.
  isRowEditable?: (inv: Invoice) => boolean;
  // "Wijzigen op het gebouw": opens the row's cost where it is managed.
  editElsewhere?: (inv: Invoice) => (() => void) | undefined;
  // Live payment status per payment-order id (from the property's payment
  // requests). When given, a cost shows "Betaald" as soon as its linked
  // payment is paid, without waiting for the stored split to be synced.
  statusByOrderId?: Record<number, OrderPaymentStatus>;
  // Hidden for a short read-only list that needs no search or filters.
  showFilters?: boolean;
  // Called with what the search and filters let through.
  onViewChange?: (view: ICostsTableView) => void;
  // A building's cost on one of its units: the amount column shows the
  // unit's share of it (see groupCostShare.utils) with the cost's whole
  // amount below it, and the totals count that share. Undefined for a row
  // that shows its own amount.
  shareFor?: (inv: Invoice) => TGroupCostUnitShare | undefined;
  // The page's unit as its building names it ("bus 2"), for the tooltip
  // that explains a share.
  shareUnitLabel?: string;
}

export const CostsTable: React.FC<CostsTableProps> = ({
  invoices: rawInvoices,
  rowLevelOf,
  propertyLabelFor,
  settledInfoFor,
  onView,
  onEdit,
  onDelete,
  isRowEditable = () => true,
  editElsewhere,
  statusByOrderId,
  showFilters = true,
  onViewChange,
  shareFor,
  shareUnitLabel,
}) => {
  const invoices = useMemo(
    () => rawInvoices.map(inv => withLivePaidSplits(inv, statusByOrderId)),
    [rawInvoices, statusByOrderId],
  );
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [yearFilter, setYearFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<ECostStatusFilter>(
    ECostStatusFilter.All,
  );

  React.useEffect(() => {
    setPage(0);
  }, [invoices.length]);

  React.useEffect(() => {
    setPage(0);
  }, [query, categoryFilter, yearFilter, statusFilter]);

  // Year options: every year a cost's period touches, newest first. The
  // year filter goes by the period (what a settlement selects on), and its
  // options say so; the export offers both rules.
  const yearOptions = React.useMemo(
    () => costYears({ invoices, basis: ECostYearBasis.Period }),
    [invoices],
  );

  // The search also finds a row by its label ("building", "privaat"), its unit
  // on the building page ("bus 2") and, across properties, by its property.
  const searchLabelOf = (invoice: Invoice) => {
    const rowLevel = rowLevelOf?.(invoice);
    return [rowLevel?.label, rowLevel?.unitLabel, propertyLabelFor?.(invoice)]
      .filter(Boolean)
      .join(" ");
  };

  const filtered = React.useMemo(
    () =>
      invoices.filter(invoice => {
        if (
          !costMatchesSearch({
            invoice,
            query,
            propertyLabel: searchLabelOf(invoice),
          })
        ) {
          return false;
        }
        if (categoryFilter && invoice.category !== categoryFilter) return false;
        if (
          yearFilter &&
          !costInYear({
            invoice,
            year: Number(yearFilter),
            basis: ECostYearBasis.Period,
          })
        ) {
          return false;
        }
        return costMatchesStatusFilter({
          status: statusOf(invoice, settledInfoFor(invoice)),
          filter: statusFilter,
        });
      }),
    [
      invoices,
      query,
      categoryFilter,
      yearFilter,
      statusFilter,
      settledInfoFor,
      rowLevelOf,
      propertyLabelFor,
    ],
  );

  const isFiltered =
    !!query.trim() ||
    !!categoryFilter ||
    !!yearFilter ||
    statusFilter !== ECostStatusFilter.All;
  const {
    count: filteredCount,
    totalExcl: filteredTotalExcl,
    pendingShareCount,
    buildingTotalExcl,
  } = costListTotals({
    invoices: filtered,
    shareFor,
    levelOf: rowLevelOf && (invoice => rowLevelOf(invoice)?.level),
  });

  // Primitives only: a parent that stores them bails out of re-rendering when
  // nothing changed, so this cannot loop.
  React.useEffect(() => {
    onViewChange?.({
      count: filteredCount,
      totalExcl: filteredTotalExcl,
      pendingShareCount,
      buildingTotalExcl,
      isFiltered,
    });
  }, [
    onViewChange,
    filteredCount,
    filteredTotalExcl,
    pendingShareCount,
    buildingTotalExcl,
    isFiltered,
  ]);

  const handleClearFilters = () => {
    setQuery("");
    setCategoryFilter("");
    setYearFilter("");
    setStatusFilter(ECostStatusFilter.All);
  };

  // One list, newest first (design 2026-10-16, D: no blocks), paged.
  const sorted = React.useMemo(
    () => sortCostsNewestFirst(filtered),
    [filtered],
  );
  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);

  // Kost | Datum | Type | Periode | Ten laste | Status | Bedrag: Type
  // only inside a building, the property in its place across properties.
  const columns = costListColumns({
    inBuilding: !!rowLevelOf,
    acrossProperties: !!propertyLabelFor,
  });
  const gridColumns = costListGridTemplate(columns);
  const pageRows = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // Outside the horizontally scrolling table, so the filters stay in view
  // when the cost grid scrolls sideways on a small screen.
  const renderToolbar = () => (
    <ListToolBar>
      <ListFilterRow>
        <div className="search">
          <Filters
            queryValue={query}
            queryPlaceholder={getLocalizedText("costs.list.search_placeholder")}
            onQueryChange={setQuery}
            onQueryClear={() => setQuery("")}
          />
        </div>
        <div className="select">
          <Select
            id="cost-filter-category"
            label="_"
            labelHidden
            value={categoryFilter}
            options={[
              {
                value: "",
                label: getLocalizedText("costs.list.filter.all_categories"),
              },
              ...sortedCategoryOptions(),
            ]}
            onChange={(opt: { value?: string } | null) =>
              setCategoryFilter(opt?.value ?? "")
            }
          />
        </div>
        <div className="select">
          <Select
            id="cost-filter-year"
            label="_"
            labelHidden
            value={yearFilter}
            options={[
              {
                value: "",
                label: getLocalizedText("costs.list.filter.all_years"),
              },
              ...yearOptions.map(year => ({
                value: String(year),
                label: getLocalizedText("costs.list.filter.period_year", {
                  year: String(year),
                }),
              })),
            ]}
            onChange={(opt: { value?: string } | null) =>
              setYearFilter(opt?.value ?? "")
            }
          />
        </div>
        <div className="select">
          <Select
            id="cost-filter-status"
            label="_"
            labelHidden
            value={statusFilter}
            // Worded like the statuses the rows show.
            options={[
              {
                value: ECostStatusFilter.All,
                label: getLocalizedText("costs.list.filter.all_statuses"),
              },
              {
                value: ECostStatusFilter.ToRequest,
                label: getLocalizedText("costs.status.to_request"),
              },
              {
                value: ECostStatusFilter.ToSettle,
                label: getLocalizedText("costs.status.to_settle"),
              },
              {
                value: ECostStatusFilter.Invited,
                label: getLocalizedText("costs.status.invitation_sent"),
              },
              {
                value: ECostStatusFilter.InSettlement,
                label: getLocalizedText("costs.status.in_settlement"),
              },
              {
                value: ECostStatusFilter.Paid,
                label: getLocalizedText("costs.status.paid"),
              },
            ]}
            onChange={(opt: { value?: ECostStatusFilter } | null) =>
              setStatusFilter(opt?.value ?? ECostStatusFilter.All)
            }
          />
        </div>
      </ListFilterRow>
    </ListToolBar>
  );

  // "GEBOUW" or "PRIVAAT" in the app's plain lozenges (costTypeAppearance),
  // a private cost's unit by its full address in the tooltip of PRIVAAT.
  const renderType = (cell: ICostListCell) => {
    if (cell.appearance !== "info" && cell.appearance !== "default") {
      return null;
    }
    return (
      <ListTypeLozenge
        label={cell.text}
        appearance={cell.appearance}
        address={cell.tooltip}
      />
    );
  };

  // As the Afrekeningen list shows its "Betaalstatus huurder": a plain
  // lozenge, with its day under it.
  const renderStatus = (cell: ICostListCell) => {
    if (!cell.appearance) return <ListSecondary>{cell.text}</ListSecondary>;
    return (
      <>
        <Tooltip tooltipContent={cell.tooltip} noCursor>
          <span className="lozenge">
            <Lozenge appearance={cell.appearance}>{cell.text}</Lozenge>
          </span>
        </Tooltip>
        {cell.subLines.map(line => (
          <ListSubLine key={line} title={line}>
            {line}
          </ListSubLine>
        ))}
      </>
    );
  };

  // A building's share on a unit page explains itself in a tooltip ("Aandeel
  // van bus 2: € 378,00 van € 1.260,00 (Duizendsten 300 / 1.000)"), which a
  // screen reader reads out after the amount.
  const renderAmount = (cell: ICostListCell) => (
    <>
      <Tooltip tooltipContent={cell.tooltip}>
        <div className={cell.isPending ? "pending" : "amount"}>
          {cell.text}
          {cell.tooltip && (
            <ListScreenReaderText>{cell.tooltip}</ListScreenReaderText>
          )}
        </div>
      </Tooltip>
      {cell.subLines.map(line => (
        <div key={line} className="sub" title={line}>
          {line}
        </div>
      ))}
    </>
  );

  const renderCellContent = (cell: ICostListCell) => {
    switch (cell.column) {
      // The title, with the supplier under it.
      case ECostListColumn.Cost:
        return (
          <ListCell>
            <ListTitle title={cell.text}>{cell.text}</ListTitle>
            {cell.subLines.map(line => (
              <ListSubLine key={line} title={line}>
                {line}
              </ListSubLine>
            ))}
          </ListCell>
        );
      case ECostListColumn.Property:
        return (
          <ListCell>
            <ListOneLine title={cell.text}>{cell.text}</ListOneLine>
          </ListCell>
        );
      case ECostListColumn.Type:
        return renderType(cell);
      // The date and the period in the Afrekeningen list's own cell, so they
      // read and wrap the same: on a narrow screen the period breaks after
      // its arrow only, the second day going to its own line.
      case ECostListColumn.InvoiceDate:
        return <ListCellText>{cell.text}</ListCellText>;
      case ECostListColumn.Period:
        return (
          <ListCellText>
            {periodRangeLines(cell.text).map((line, index) => (
              <React.Fragment key={line}>
                {index > 0 && " "}
                <span className="nowrap">{line}</span>
              </React.Fragment>
            ))}
          </ListCellText>
        );
      case ECostListColumn.ChargedTo:
        return (
          <ListCell>
            <Tooltip tooltipContent={cell.tooltip} noCursor>
              <ListSecondary>{cell.text}</ListSecondary>
            </Tooltip>
          </ListCell>
        );
      case ECostListColumn.Status:
        return <ListLozengeBox>{renderStatus(cell)}</ListLozengeBox>;
      case ECostListColumn.Amount:
        return <ListAmount>{renderAmount(cell)}</ListAmount>;
    }
  };

  const renderCell = (cell: ICostListCell) => (
    <ListAligned key={cell.column} $align={costListColumnAlign(cell.column)}>
      {renderCellContent(cell)}
    </ListAligned>
  );

  const rowActions = (inv: Invoice) => {
    // A cost inside a confirmed settlement is untouchable: edit is hidden
    // here and delete is blocked in the handler — the settlement has to be
    // deleted first. A cost a departure settled part of stays editable. A
    // cost with a payment request opens in a limited edit that only corrects
    // the invoice details. A row managed elsewhere links to where it is
    // changed.
    const isSettled = settledInfoFor(inv).settled;
    const isEditable = isRowEditable(inv);
    const openElsewhere = isSettled ? undefined : editElsewhere?.(inv);
    const actions = [
      {
        content: getLocalizedText("costs.action.view"),
        onClick: () => onView(inv),
      },
      ...(onEdit && isEditable && !isSettled
        ? [
            {
              content: getLocalizedText("costs.action.edit_item"),
              onClick: () => onEdit(inv),
            },
          ]
        : []),
      ...(!isEditable && openElsewhere
        ? [
            {
              content: getLocalizedText("costs.action.edit_on_building"),
              onClick: openElsewhere,
            },
          ]
        : []),
    ];
    if (onDelete && isEditable) {
      actions.push({
        content: getLocalizedText("costs.action.delete"),
        onClick: () => onDelete(inv),
      });
    }
    return actions;
  };

  const renderRow = (inv: Invoice) => (
    <ListRow key={inv.id} $columns={gridColumns} onClick={() => onView(inv)}>
      {costListRowCells({
        invoice: inv,
        columns,
        rowLevel: rowLevelOf?.(inv),
        propertyLabel: propertyLabelFor?.(inv),
        share: shareFor?.(inv),
        shareUnitLabel,
        settled: settledInfoFor(inv),
        format: formatMoney,
      }).map(renderCell)}
      <div onClick={e => e.stopPropagation()}>
        <DropdownMenu actions={rowActions(inv)}>
          <ListMenuButton
            type="button"
            aria-label={getLocalizedText("costs.action.more")}
          >
            <Icon source="navigationMenuVertical" />
          </ListMenuButton>
        </DropdownMenu>
      </div>
    </ListRow>
  );

  const renderNoResults = () => (
    <EmptyState
      heading={getLocalizedText("search.no_results")}
      icon="billSplit"
      action={{
        content: getLocalizedText("application.filters.panel.clear"),
        onClick: handleClearFilters,
        appearance: "outline",
      }}
    />
  );

  return (
    <>
      {showFilters && renderToolbar()}
      {filtered.length === 0 && invoices.length > 0 ? (
        renderNoResults()
      ) : (
        <Wrap>
          {/* The Afrekeningen list's header, so the two read the same. */}
          <ListHeaderRow $columns={gridColumns} $spread>
            {columns.map(column => (
              <ListHeaderCell key={column} $align={costListColumnAlign(column)}>
                {column === ECostListColumn.Type ? (
                  <TypeColumnHeader kind="cost" />
                ) : (
                  costListColumnHeader(column)
                )}
              </ListHeaderCell>
            ))}
            <ListHeaderCell />
          </ListHeaderRow>
          {pageRows.map(renderRow)}
        </Wrap>
      )}
      {/* Outside the table's frame, which reaches beyond the card's content. */}
      {filtered.length > 0 && totalPages > 1 && (
        <Pagination
          pageCount={totalPages}
          initialPage={page}
          onPageChange={({ selected }: { selected: number }) =>
            setPage(selected)
          }
        />
      )}
    </>
  );
};
